/**
 * Validation Rules
 *
 * Purpose: Business rules and validation logic for FSS calculator
 * Usage: Input validation, business rule enforcement
 * Features: Special case handling, error messages
 */
// import necessary data
// remove unused import  import { stateData, industryData, businessTypes } from '../data';

 import { stateData, businessTypes } from '../data';

/**
 * Validation rules for state and industry combinations
 */
const validationRules = {
  /**
   * Validate state selection
   * @param {string} stateCode - Selected state code
   * @returns {object} Validation result
   */
  validateState: (stateCode) => {
    const errors = [];
    const warnings = [];

    // Check if state exists
    if (!stateCode || stateCode.trim() === '') {
      errors.push('State selection is required');
      return { isValid: false, errors, warnings };
    }

    // Check if state is valid
    if (!stateData[stateCode]) {
      errors.push(`Invalid state code: ${stateCode}`);
      return { isValid: false, errors, warnings };
    }

    // Special case: Victoria not legally mandated
    if (stateCode === 'VIC') {
      warnings.push({
        type: 'info',
        message: 'FSS is not legally mandated in Victoria, but councils and auditors expect documented training',
        rule: 'VIC_NOT_MANDATED'
      });
    }

    return {
      isValid: true,
      errors,
      warnings
    };
  },

  /**
   * Validate industry selection
   * @param {string} businessTypeId - Selected business type ID
   * @param {string} stateCode - Selected state (for context)
   * @returns {object} Validation result
   */
  validateIndustry: (businessTypeId, stateCode) => {
    const errors = [];
    const warnings = [];

    // Check if business type exists
    if (!businessTypeId || businessTypeId.trim() === '') {
      errors.push('Business type selection is required');
      return { isValid: false, errors, warnings };
    }

    const businessType = businessTypes.find(bt => bt.id === businessTypeId);
    if (!businessType) {
      errors.push(`Invalid business type ID: ${businessTypeId}`);
      return { isValid: false, errors, warnings };
    }

    // Check for state restrictions (Food Processing)
    if (businessType.stateRestricted && businessType.stateAvailability) {
      if (!businessType.stateAvailability.includes(stateCode)) {
        warnings.push({
          type: 'warning',
          message: 'Specific training units for this business type are not available in your state. Alternative units will be recommended.',
          rule: 'FOOD_PROCESSING_STATE_RESTRICTION'
        });
      }
    }

    // Check for sub-selection requirements (Aged Care)
    if (businessType.requiresSubSelection) {
      warnings.push({
        type: 'info',
        message: businessType.subSelectionNote || 'Additional classification may be required',
        rule: 'REQUIRES_SUB_SELECTION'
      });
    }

    return {
      isValid: true,
      errors,
      warnings
    };
  },

  /**
   * Check for special business rules
   * @param {string} stateCode - Selected state
   * @param {string} businessTypeId - Selected business type ID
   * @returns {array} Special warnings or messages
   */
  getSpecialRules: (stateCode, businessTypeId) => {
    const specialRules = [];
    const businessType = businessTypes.find(bt => bt.id === businessTypeId);
    const state = stateData[stateCode];

    if (!businessType || !state) {
      return specialRules;
    }

    // Rule 1: NSW Aged Care Special Case
    if (stateCode === 'NSW' && businessTypeId.includes('agedCare')) {
      specialRules.push({
        rule: 'NSW_AGED_CARE',
        type: 'warning',
        title: 'NSW Aged Care Requirements',
        message: 'NSW has different requirements for licensed and non-licensed aged care facilities. Licensed facilities require a written Food Safety Program, while non-licensed facilities only accept SITX units.',
        action: 'Please confirm your facility licensing status'
      });
    }

    // Rule 2: Victoria Not Legally Mandated
    if (stateCode === 'VIC') {
      specialRules.push({
        rule: 'VIC_NOT_MANDATED',
        type: 'info',
        title: 'Victoria: Not Legally Mandated',
        message: state.fssRequiredNote,
        action: 'We strongly recommend obtaining FSS certification'
      });
    }

    // Rule 3: QLD Council Notification
    if (stateCode === 'QLD' && state.councilNotification) {
      specialRules.push({
        rule: 'QLD_COUNCIL_NOTIFICATION',
        type: 'warning',
        title: 'QLD Council Notification Required',
        message: state.councilNotificationDetails,
        action: 'Remember to notify your local council within 14 days'
      });
    }

    // Rule 4: Food Processing State Availability
    if (businessTypeId.includes('foodProcessing') || businessTypeId.includes('brewery')) {
      if (!['VIC', 'QLD'].includes(stateCode)) {
        specialRules.push({
          rule: 'FOOD_PROCESSING_LIMITED',
          type: 'warning',
          title: 'Limited Training Unit Availability',
          message: 'FBPFSY training units are only available in Victoria and Queensland. Alternative SITX units will be recommended for your state.',
          action: 'Alternative units will be displayed in your results'
        });
      }
    }

    // Rule 5: Vulnerable Population Businesses
    if (businessType.vulnerablePopulation) {
      specialRules.push({
        rule: 'VULNERABLE_POPULATION',
        type: 'warning',
        title: 'Vulnerable Population Service',
        message: 'Your business serves vulnerable populations (children, elderly, patients). Extra care and stricter compliance requirements apply.',
        action: 'Ensure all staff are properly trained'
      });
    }

    // Rule 6: High-Risk Business Category
    if (businessType.highRisk) {
      specialRules.push({
        rule: 'HIGH_RISK_BUSINESS',
        type: 'info',
        title: 'High-Risk Food Business',
        message: 'Your business is classified as Category 1 (high-risk). More stringent food safety requirements and regular audits apply.',
        action: 'Maintain comprehensive food safety records'
      });
    }

    // Rule 7: NSW Certificate Display
    if (stateCode === 'NSW' && state.displayRequired) {
      specialRules.push({
        rule: 'NSW_DISPLAY_REQUIRED',
        type: 'info',
        title: 'Certificate Display Required',
        message: `In NSW, your FSS certificate must be ${state.displayDetails}`,
        action: 'Display certificate in a visible location'
      });
    }

    // Rule 8: Standard 3.2.2A Deadline Check
    const today = new Date();
    const deadline = new Date(state.standardDeadline);
    if (today > deadline) {
      specialRules.push({
        rule: 'DEADLINE_PASSED',
        type: 'error',
        title: 'Compliance Deadline Passed',
        message: `Standard 3.2.2A compliance deadline has passed (${state.standardDeadline}). Your business may not be compliant.`,
        action: 'Obtain FSS certification as soon as possible'
      });
    }

    return specialRules;
  },

  /**
   * Validate complete selection (state + business type)
   * @param {string} stateCode - Selected state
   * @param {string} businessTypeId - Selected business type ID
   * @returns {object} Complete validation result
   */
  validateSelection: (stateCode, businessTypeId) => {
    const stateValidation = validationRules.validateState(stateCode);
    const industryValidation = validationRules.validateIndustry(businessTypeId, stateCode);
    const specialRules = validationRules.getSpecialRules(stateCode, businessTypeId);

    const allErrors = [...stateValidation.errors, ...industryValidation.errors];
    const allWarnings = [
      ...stateValidation.warnings,
      ...industryValidation.warnings,
      ...specialRules
    ];

    return {
      isValid: allErrors.length === 0,
      errors: allErrors,
      warnings: allWarnings,
      specialRules: specialRules
    };
  },

  /**
   * Check if aged care sub-selection is required
   * @param {string} businessTypeId - Business type ID
   * @returns {boolean} True if sub-selection required
   */
  requiresAgedCareSubSelection: (businessTypeId) => {
    return businessTypeId === 'agedCareFacility';
  },

  /**
   * Get available aged care options for sub-selection
   * @returns {array} Aged care facility types
   */
  getAgedCareOptions: () => {
    return [
      {
        id: 'agedCareLicensed',
        name: 'Licensed Aged Care Facility',
        description: 'Facility with government license requiring written Food Safety Program'
      },
      {
        id: 'agedCareNonLicensed',
        name: 'Non-Licensed Aged Care Facility',
        description: 'Facility without government license, SITX units accepted'
      }
    ];
  },

  /**
   * Validate training units for state
   * @param {array} unitCodes - Training unit codes
   * @param {string} stateCode - State code
   * @returns {object} Validation result
   */
  validateTrainingUnits: (unitCodes, stateCode) => {
    const errors = [];
    const warnings = [];

    // Check if FBPFSY units are used outside VIC/QLD
    const hasFoodProcessingUnits = unitCodes.some(code => code.startsWith('FBPFSY'));
    if (hasFoodProcessingUnits && !['VIC', 'QLD'].includes(stateCode)) {
      errors.push('FBPFSY training units are not available in your state');
      warnings.push({
        type: 'warning',
        message: 'Alternative SITX units should be used instead',
        rule: 'TRAINING_UNIT_STATE_RESTRICTION'
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }
};

/**
 * Error messages for different validation failures
 */
export const ERROR_MESSAGES = {
  STATE_REQUIRED: 'Please select your state or territory',
  STATE_INVALID: 'Invalid state selection',
  BUSINESS_TYPE_REQUIRED: 'Please select your business type',
  BUSINESS_TYPE_INVALID: 'Invalid business type selection',
  COMBINATION_INVALID: 'This combination of state and business type requires special consideration'
};

/**
 * Warning messages for special cases
 */
export const WARNING_MESSAGES = {
  VIC_NOT_MANDATED: 'FSS is not legally mandated in Victoria, but strongly recommended',
  NSW_AGED_CARE: 'NSW aged care has specific requirements based on licensing status',
  QLD_COUNCIL_NOTIFICATION: 'QLD requires council notification within 14 days',
  FOOD_PROCESSING_LIMITED: 'Limited training units available in your state',
  HIGH_RISK_BUSINESS: 'High-risk businesses have stricter compliance requirements',
  VULNERABLE_POPULATION: 'Extra care required when serving vulnerable populations'
};

export default validationRules;
